// §3 contract of docs/plans/accounts-sync-status-model.md — the TypeScript
// mirror of the retired Rust crate's src/services/sources/status.rs (serde output = wire).
//
// Laws: L1 — zero optional fields ("sometimes present" = its own variant).
// L2 — an Account value exists only fully authenticated. L3 — exhaustive
// tagged unions; every switch below has a never-default. L4 — the ONLY wire
// method is `source.status.list`; the client derives nothing beyond the
// pure render functions in this file.
//
// @tested-by: tst_fe_status_golden_001..004, tst_fe_accounts_011
// @invariant: INV-ST-9 — golden fixtures shared with the Rust matrix test

// ─── источник и аккаунты ────────────────────────────────────────────────────
import { relTime } from "../utils/time";

import type {
  Account,
  AuthLossReason,
  RepairAction,
  SourceStatus,
  SurfaceSync,
  SyncError,
} from "@magnis/client-core";

export type {
  Account,
  AccountState,
  AuthLossReason,
  Cursor,
  ItemCount,
  PriorSuccess,
  RepairAction,
  SourceAuthKind,
  SourceStatus,
  SourceStatusListResponse,
  SurfaceSync,
  SyncError,
  SyncReport,
  SyncRun,
  TotalEstimate,
  VerifiedAuth,
} from "@magnis/client-core";

// ─── чистый рендер контракта (план §3 «Рендер строки») ─────────────────────

export type Tone = "green" | "blue" | "amber" | "red" | "grey";

export interface Badge {
  readonly label: string;
  readonly tone: Tone;
}

export function authLossText(reason: AuthLossReason): string {
  switch (reason.reason) {
    case "oauth_expired":
      return "Authorization expired";
    case "oauth_revoked":
      return "Access revoked";
    case "session_lost":
      return "Session lost";
    case "key_rejected":
      return `Key rejected: ${reason.provider_message}`;
    case "key_removed":
      return "API key removed";
    default: {
      const never: never = reason;
      throw new Error(`unknown auth-loss reason ${JSON.stringify(never)}`);
    }
  }
}

/** «Reconnect» surfaces ONLY through oauth repairs (wording law, §3). */
export function repairLabel(repair: RepairAction): string {
  switch (repair) {
    case "reconnect_oauth":
      return "Reconnect";
    case "relogin_phone":
      return "Re-login";
    case "enter_key":
      return "Enter key";
    case "replace_key":
      return "Replace key";
    default: {
      const never: never = repair;
      throw new Error(`unknown repair ${JSON.stringify(never)}`);
    }
  }
}

function syncErrorLabel(error: SyncError): string {
  switch (error.kind) {
    case "auth":
      return "Auth error";
    case "network":
      return "Network error";
    case "contract":
      return "Connector error";
    case "internal":
      return "Internal error";
    default: {
      const never: never = error;
      throw new Error(`unknown sync error ${JSON.stringify(never)}`);
    }
  }
}

/** The human-readable detail of a failed surface (empty when numeric-only). */
export function syncErrorDetail(error: SyncError): string {
  switch (error.kind) {
    case "auth":
    case "network":
    case "contract":
    case "internal":
      return error.message;
    default: {
      const never: never = error;
      throw new Error(`unknown sync error ${JSON.stringify(never)}`);
    }
  }
}

/** Per-surface badge — a total function of the SurfaceSync variant. */
export function surfaceBadge(surface: SurfaceSync, now: Date): Badge {
  switch (surface.state) {
    case "never_synced":
      // A just-connected surface whose bootstrap is queued — reads as pending,
      // not dead. The state flips to `bootstrapping` on the first sync report.
      return { label: "Preparing to sync…", tone: "blue" };
    case "bootstrapping":
    case "catching_up":
      return { label: "Syncing", tone: "blue" };
    case "synced":
      return { label: `Synced ${relTime(surface.last_success.finished_at, now)}`, tone: "green" };
    case "live":
      return { label: `Live · ${relTime(surface.last_event_at, now)}`, tone: "green" };
    case "rate_limited":
      return { label: "Rate limited", tone: "amber" };
    case "failed":
      return { label: syncErrorLabel(surface.error), tone: "red" };
    default: {
      const never: never = surface;
      throw new Error(`unknown surface state ${JSON.stringify(never)}`);
    }
  }
}

/**
 * What a progress bar may render — a DISCRIMINATED union so the UI is
 * structurally incapable of pairing `fetched`/`ingested` (envelopes) with
 * `total` (primary items): the raw SyncRun never reaches the bar.
 *
 *   - determinate:   `done / total(+)` — done IS `discovered` (same units as
 *                    total, universal-sync-progress DEC-2/DEC-3). Requires
 *                    discovered > 0 AND total > 0.
 *   - indeterminate: "N synced…" — N IS `discovered`; total unknown.
 *   - none:          no same-unit number exists (discovered unreported) OR
 *                    the surface is not bootstrapping — render the phase
 *                    label only, no numeric pair, no bar.
 */
export type RunProgress =
  | {
      readonly kind: "determinate";
      readonly done: number;
      readonly total: number;
      readonly atLeast: boolean;
    }
  | { readonly kind: "indeterminate"; readonly discovered: number }
  | { readonly kind: "none" };

/**
 * The ONLY path from a surface to a progress bar. Per universal-sync-progress
 * DEC-3 the bar exists during BOOTSTRAP only — catching_up/live keep the phase
 * label (no denominator: a 30s catchup cycle has no meaningful "x / total").
 */
export function surfaceProgress(surface: SurfaceSync): RunProgress {
  switch (surface.state) {
    case "bootstrapping": {
      const run = surface.run;
      if (run.discovered > 0 && run.total.state !== "unknown" && run.total.value > 0) {
        return {
          kind: "determinate",
          done: run.discovered,
          total: run.total.value,
          atLeast: run.total.state === "at_least",
        };
      }
      if (run.discovered > 0) {
        return { kind: "indeterminate", discovered: run.discovered };
      }
      // No discovered reported: envelopes (`fetched`) are NOT progress —
      // no numeric pair, no bar (the "27979 / 2593+" incident).
      return { kind: "none" };
    }
    case "catching_up":
    case "never_synced":
    case "synced":
    case "live":
    case "rate_limited":
    case "failed":
      return { kind: "none" };
    default: {
      const never: never = surface;
      throw new Error(`unknown surface state ${JSON.stringify(never)}`);
    }
  }
}

/** The settled badge number: the surface's exact graph item count, or null
 *  (render NOTHING — never an envelope counter). */
export function itemCount(surface: SurfaceSync): number | null {
  switch (surface.state) {
    case "synced":
    case "live":
      return surface.items.state === "exact" ? surface.items.value : null;
    case "never_synced":
    case "bootstrapping":
    case "catching_up":
    case "rate_limited":
    case "failed":
      return null;
    default: {
      const never: never = surface;
      throw new Error(`unknown surface state ${JSON.stringify(never)}`);
    }
  }
}

function isSyncedWord(surface: SurfaceSync): boolean {
  return surface.state === "synced" || surface.state === "live";
}

function lastSuccessAt(surface: SurfaceSync): string | null {
  switch (surface.state) {
    case "synced":
      return surface.last_success.finished_at;
    case "live":
      return surface.last_event_at;
    case "never_synced":
    case "bootstrapping":
    case "catching_up":
    case "rate_limited":
    case "failed":
      return null;
    default: {
      const never: never = surface;
      throw new Error(`unknown surface state ${JSON.stringify(never)}`);
    }
  }
}

/**
 * Account row badge — the §3 render matrix, top to bottom:
 * auth_lost → reason (amber); any failed → Error (red); any run in flight →
 * Syncing (blue); rate-limited without a synced surface → amber; all
 * never_synced → grey; otherwise «Synced <max(last_success)>» — the word
 * produced ONLY by synced|live variants.
 */
export function accountBadge(account: Account, now: Date): Badge {
  if (account.state.state === "auth_lost") {
    return { label: authLossText(account.state.reason), tone: "amber" };
  }
  if (account.surfaces.some((s) => s.state === "failed")) {
    return { label: "Error", tone: "red" };
  }
  if (account.surfaces.some((s) => s.state === "bootstrapping" || s.state === "catching_up")) {
    return { label: "Syncing", tone: "blue" };
  }
  if (!account.surfaces.some(isSyncedWord)) {
    if (account.surfaces.some((s) => s.state === "rate_limited")) {
      return { label: "Rate limited", tone: "amber" };
    }
    return { label: "Preparing to sync…", tone: "blue" };
  }
  const newest = account.surfaces
    .map(lastSuccessAt)
    .filter((t): t is string => t !== null)
    .sort()
    .at(-1);
  if (newest === undefined) {
    // Unreachable: isSyncedWord guarantees a last-success timestamp.
    throw new Error("synced|live surface without a success timestamp");
  }
  return { label: `Synced ${relTime(newest, now)}`, tone: "green" };
}

/**
 * Group-header counters — all three numbers from the ONE collection the
 * detail view renders (правило №4 оператора): K accounts · N surfaces ·
 * M synced, where M counts synced|live only.
 */
export function sourceCounterLine(source: SourceStatus): string {
  const k = source.accounts.length;
  const n = source.accounts.reduce((sum, a) => sum + a.surfaces.length, 0);
  const m = source.accounts.reduce(
    (sum, a) => sum + a.surfaces.filter(isSyncedWord).length,
    0,
  );
  const accounts = k === 1 ? "account" : "accounts";
  const surfaces = n === 1 ? "surface" : "surfaces";
  return `${String(k)} ${accounts} · ${String(n)} ${surfaces} · ${String(m)} synced`;
}
